import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../contexts/NotificationContext';
import { useEmailService } from '../../hooks/useEmailService'; // ADD THIS IMPORT
import Loading from '../common/Loading';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import AvatarUpload from '../common/AvatarUpload';
import { ISSUE_STATUS, ISSUE_PRIORITY, ISSUE_TYPE, STATUS_OPTIONS, PRIORITY_OPTIONS, TYPE_OPTIONS } from '../../utils/constants';
import { formatDateTime, getRelativeTime } from '../../utils/helpers';

const IssueDetail = () => {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { notify } = useNotification();
  const { sendIssueAssignmentEmail } = useEmailService(); // ADD THIS HOOK
  const [issue, setIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    const fetchIssueData = async () => {
      try {
        // Fetch issue
        const issueDoc = await getDoc(doc(db, 'issues', issueId));
        if (!issueDoc.exists()) {
          notify.error('Issue not found');
          navigate('/issues');
          return;
        }

        const issueData = {
          id: issueDoc.id,
          ...issueDoc.data()
        };
        setIssue(issueData);
        setEditForm(issueData);

        // Fetch comments
        const commentsQuery = query(
          collection(db, 'comments'),
          where('issueId', '==', issueId),
          orderBy('createdAt', 'asc')
        );
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsData = commentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setComments(commentsData);

        // Fetch users for assignee dropdown
        await fetchUsers();

      } catch (error) {
        console.error('Error fetching issue:', error);
        notify.error('Failed to load issue');
        navigate('/issues');
      } finally {
        setLoading(false);
      }
    };

    fetchIssueData();
  }, [issueId, navigate, notify]);

  // Add function to fetch users
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      notify.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUpdateIssue = async (e) => {
    e.preventDefault();
    if (!issue) return;

    try {
      setSaving(true);
      
      // Get the selected user data
      const selectedUser = users.find(user => user.uid === editForm.assigneeId);
      
      // Check if assignee changed
      const assigneeChanged = issue.assigneeId !== editForm.assigneeId;
      console.log('🔄 Assignee changed:', assigneeChanged, 'from:', issue.assigneeId, 'to:', editForm.assigneeId);

      const updateData = {
        ...editForm,
        assigneeName: selectedUser ? selectedUser.displayName : null,
        assigneeEmail: selectedUser ? selectedUser.email : null,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'issues', issue.id), updateData);
      
      // If assignee changed and new assignee exists, send email
      if (assigneeChanged && selectedUser && selectedUser.email) {
        console.log('📧 Sending assignment email to:', selectedUser.email);
        try {
          await sendIssueAssignmentEmail(
            { ...issue, ...updateData },
            selectedUser.email,
            selectedUser.displayName
          );
          console.log('✅ Assignment email sent successfully');
        } catch (emailError) {
          console.error('❌ Failed to send assignment email:', emailError);
          // Don't throw here - we don't want to block the issue update
          notify.error('Issue updated but failed to send notification email');
        }
      }
      
      setIssue(prev => ({ ...prev, ...updateData }));
      setEditing(false);
      notify.success('Issue updated successfully');
    } catch (error) {
      console.error('Error updating issue:', error);
      notify.error('Failed to update issue');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIssue = async () => {
    if (!window.confirm('Are you sure you want to delete this issue? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'issues', issueId));
      notify.success('Issue deleted successfully');
      navigate('/issues');
    } catch (error) {
      console.error('Error deleting issue:', error);
      notify.error('Failed to delete issue');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      const commentData = {
        issueId,
        text: commentText.trim(),
        createdBy: userProfile.uid,
        authorName: userProfile.displayName,
        authorAvatar: userProfile.avatarUrl,
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'comments'), commentData);
      const newComment = {
        id: docRef.id,
        ...commentData
      };

      setComments(prev => [...prev, newComment]);
      setCommentText('');
      notify.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      notify.error('Failed to add comment');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!issue) return;

    try {
      await updateDoc(doc(db, 'issues', issue.id), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      
      setIssue(prev => ({ ...prev, status: newStatus }));
      setEditForm(prev => ({ ...prev, status: newStatus }));
      notify.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      notify.error('Failed to update status');
    }
  };

  // Generate assignee options for dropdown
  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...users.map(user => ({
      value: user.uid,
      label: user.displayName || user.email
    }))
  ];

  if (loading) {
    return <Loading text="Loading issue..." />;
  }

  if (!issue) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Issue not found</h2>
        <Button onClick={() => navigate('/issues')} className="mt-4">
          Back to Issues
        </Button>
      </div>
    );
  }

  const canEdit = issue.createdBy === userProfile?.uid || userProfile?.role === 'admin';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/issues')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2"
          >
            ← Back to Issues
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {issue.issueKey}: {issue.title}
          </h1>
        </div>
        
        <div className="flex space-x-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={() => setEditing(!editing)}
              >
                {editing ? 'Cancel Edit' : 'Edit Issue'}
              </Button>
              <Button
                variant="danger"
                loading={deleting}
                onClick={handleDeleteIssue}
              >
                Delete Issue
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Description</h2>
            {editing ? (
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
                placeholder="Enter issue description..."
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {issue.description || 'No description provided.'}
              </p>
            )}
          </div>

          {/* Comments */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Comments</h2>
            
            {/* Add Comment */}
            <form onSubmit={handleAddComment} className="mb-6">
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <AvatarUpload
                    userId={userProfile.uid}
                    currentAvatarUrl={userProfile.avatarUrl}
                    displayName={userProfile.displayName}
                    size="small"
                    showRemoveButton={false}
                  />
                </div>
                <div className="flex-1">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      type="submit"
                      disabled={!commentText.trim()}
                    >
                      Add Comment
                    </Button>
                  </div>
                </div>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No comments yet.</p>
              ) : (
                comments.map(comment => (
                  <div key={comment.id} className="flex space-x-4">
                    <div className="flex-shrink-0">
                      {comment.authorAvatar ? (
                        <img
                          src={comment.authorAvatar}
                          alt={comment.authorName}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {comment.authorName?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {comment.authorName}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {getRelativeTime(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <Select
                  value={issue.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  options={STATUS_OPTIONS}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>

          {/* Issue Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Details</h3>
            
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Type:</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-white capitalize">
                  {issue.type}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority:</span>
                <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                  issue.priority === 'low' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                  issue.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                  issue.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                }`}>
                  {issue.priority}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Assignee:</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  {issue.assigneeName || 'Unassigned'}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Reporter:</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  {issue.creatorName}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Created:</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  {formatDateTime(issue.createdAt)}
                </span>
              </div>
              
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Updated:</span>
                <span className="ml-2 text-sm text-gray-900 dark:text-white">
                  {getRelativeTime(issue.updatedAt)}
                </span>
              </div>
              
              {issue.storyPoints && (
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Story Points:</span>
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">
                    {issue.storyPoints}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Edit Form (when editing) */}
          {editing && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Issue</h3>
              
              <form onSubmit={handleUpdateIssue} className="space-y-4">
                <Input
                  label="Title"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
                
                <Select
                  label="Type"
                  value={editForm.type || 'task'}
                  onChange={(e) => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                  options={TYPE_OPTIONS}
                />
                
                <Select
                  label="Priority"
                  value={editForm.priority || 'medium'}
                  onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                  options={PRIORITY_OPTIONS}
                />

                {/* Add Assignee Select Field */}
                <Select
                  label="Assignee"
                  value={editForm.assigneeId || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, assigneeId: e.target.value }))}
                  options={assigneeOptions}
                  disabled={usersLoading}
                />
                
                <Input
                  label="Story Points"
                  type="number"
                  value={editForm.storyPoints || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, storyPoints: e.target.value }))}
                  min="0"
                />
                
                <div className="flex space-x-2">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={saving}
                    className="flex-1"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueDetail;